import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Tabs, Button, Table, Spin, message } from 'antd';
import constants from '../../../constants/constants';
import { fetchAdminApi } from '../../../services/api';

const useResponsiveTabs = () => {
	const getTabSize = () => window.innerWidth < 768 ? 'small' : 'large';
	const [tabSize, setTabSize] = useState(getTabSize);

	useEffect(() => {
		const handleResize = () => setTabSize(getTabSize());
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	return tabSize;
};

const TableWrapper = ({ clients, enquiries, columns }) => {
	const buildMergedData = (sortedClients, sortedEnquiries) => {
		const maxRows = Math.max(sortedClients.length, sortedEnquiries.length);
		return Array.from({ length: maxRows }, (_, i) => ({
			key: i,
			client: sortedClients[i] || null,
			enquiry: sortedEnquiries[i] || null,
		}));
	};

	const [mergedData, setMergedData] = useState(() => buildMergedData(clients, enquiries));

	const handleChange = (_, __, sorter) => {
		if (!sorter.order) {
			setMergedData(buildMergedData(clients, enquiries));
			return;
		}

		const dir = sorter.order === "ascend" ? 1 : -1;

		if (sorter.columnKey === "client") {
			const sorted = [...clients].sort((a, b) =>
				dir * (a.name || "").localeCompare(b.name || "")
			);
			setMergedData(buildMergedData(sorted, enquiries));
		} else if (sorter.columnKey === "enquiry") {
			const sorted = [...enquiries].sort((a, b) =>
				dir * (a.client_name || "").localeCompare(b.client_name || "")
			);
			setMergedData(buildMergedData(clients, sorted));
		}
	};

	return (
		<div className="w-full xl:max-w-5xl xl:mx-auto">
			<Table
				columns={columns}
				dataSource={mergedData}
				rowKey="key"
				pagination={false}
				size="small"
				onChange={handleChange}
				locale={{ emptyText: "No records found." }}
			/>
		</div>
	);
};

const AssociatesDashboard = () => {
	const history = useHistory();
	const tabSize = useResponsiveTabs();

	const [clientList, setClientList] = useState([]);
	const [enquiryList, setEnquiryList] = useState([]);
	const [associates, setAssociates] = useState([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		fetchAll();
	}, []);

	const fetchAll = () => {
		setLoading(true);

		Promise.all([
			fetchAdminApi(`/dashboard`, "post", { type: "1,3" }),
			fetchAdminApi(`/client-enquiry`, "post", { type: "1" })
		])
			.then(([clientRes, enquiryRes]) => {
				const clients = clientRes?.data || [];
				const enquiries = enquiryRes?.data || [];

				if (!clientRes?.data) message.error(clientRes?.message || "Failed to load clients.");
				if (!enquiryRes?.data) message.error(enquiryRes?.message || "Failed to load enquiries.");

				setClientList(clients);
				setEnquiryList(enquiries);

				const therapistSet = new Set();
				[...clients, ...enquiries].forEach(record => {
					if (record?.associate_name) {
						therapistSet.add(record.associate_name.trim());
					}
					if (record?.shared_name) {
						record.shared_name.split(",").forEach(n => {
							therapistSet.add(n.trim());
						});
					}
				});

				const list = Array.from(therapistSet);
				list.sort((a, b) => {
					const aIsRole1 = [...clients, ...enquiries].some(
						c => c.associate_name?.trim() === a && c.associate_role === "1"
					);
					const bIsRole1 = [...clients, ...enquiries].some(
						c => c.associate_name?.trim() === b && c.associate_role === "1"
					);
					return (bIsRole1 ? 1 : 0) - (aIsRole1 ? 1 : 0);
				});

				setAssociates(list);
			})
			.catch(() => {
				message.error("Failed to load data. Please try again.");
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const routeToEditClientEnquiry = (record) => {
		if (record?.status === "4" || record?.status === "9") {
			sessionStorage.removeItem("enquiryDetails");
			history.push({ pathname: "/edit-client", state: { client_id: record?.client_id } });
		} else {
			sessionStorage.setItem("enquiryDetails", JSON.stringify(record));
			history.push({
				pathname: "/edit-enquiry",
				state: {
					enquiry_id: record?.enquiry_id,
					show_other_details: record?.show_other_details
				}
			});
		}
	};

	const tabItems = associates.map(name => {
		const clients = clientList.filter(client => {
			const associateMatch = client.associate_name === name;
			const sharedMatch =
				client.shared_name &&
				client.shared_name.split(",").map(n => n.trim()).includes(name);
			return associateMatch || sharedMatch;
		});

		const enquiries = enquiryList.filter(enq => {
			const associateMatch = enq.associate_name === name;
			const sharedMatch =
				enq.shared_name &&
				enq.shared_name.split(",").map(n => n.trim()).includes(name);
			return associateMatch || sharedMatch;
		});

		if (clients.length === 0 && enquiries.length === 0) return null;

		const columns = [
			clients.length > 0 && {
				title: "Client Name",
				dataIndex: "client",
				key: "client",
				width: "50%",
				sorter: true,
				render: (client) => client ? (
					<a onClick={() => {
						sessionStorage.removeItem("enquiryDetails");
						history.push({ pathname: "/edit-client", state: { client_id: client.client_id } });
					}}>
						{client.name || "-"}
					</a>
				) : "-"
			},
			enquiries.length > 0 && {
				title: "Enquiry Name",
				dataIndex: "enquiry",
				key: "enquiry",
				width: "50%",
				sorter: true,
				render: (enquiry) => enquiry ? (
					<a onClick={() => routeToEditClientEnquiry(enquiry)}>
						{enquiry.client_name || "-"}
					</a>
				) : "-"
			},
		].filter(Boolean);

		return {
			label: name,
			key: name,
			children: <TableWrapper clients={clients} enquiries={enquiries} columns={columns} />
		};
	}).filter(Boolean);

	return (
		<div>
			<div className="text-center bg-amber-300 text-lg font-semibold p-3">
				{constants.Subtitle}
				<span className="py-0.5 px-3 ml-3 text-sm bg-white rounded-xl capitalize">
					{constants.Associates} Dashboard
				</span>
			</div>
			{history.length > 1 && (
				<div className='bg-amber-100 text-base font-semibold p-1.5 mb-3'>
					<div className="container mx-auto">
						<Button
							size='small'
							className='text-xs font-semibold bg-sky-800 text-white uppercase'
							onClick={() => history.goBack()}
						>
							{constants.BackBtn}
						</Button>
					</div>
				</div>
			)}
			<div className="container mx-auto">
				<div className="mx-6 sm:mx-0 mt-6 mb-6">
					<Spin spinning={loading}>
						<Tabs size={tabSize} items={tabItems} defaultActiveKey={tabItems[0]?.key} />
					</Spin>
				</div>
			</div>
		</div>
	);
};

export default AssociatesDashboard;